/**
 * configure — @linkup/api-hooks 전역 설정 주입
 *
 * 앱 최상단(providers, _app.tsx 등)에서 한 번만 호출한다.
 *
 * @example
 * configure({
 *   axios: myAxiosInstance,
 *   onError: (err) => toast.error(getErrorMessage(err)),
 *   onPendingChange: (delta) => setPendingCount((c) => c + delta),
 *   extractData: (res) => res.data.data,
 * });
 */
import type { AxiosInstance, AxiosResponse } from "axios";

export interface ApiHooksConfig {
    /** 프로젝트에서 사용하는 axios 인스턴스 (인터셉터 포함) */
    axios: AxiosInstance;
    /**
     * API 에러 발생 시 호출되는 핸들러.
     * toast, 로깅 등을 여기서 처리한다.
     */
    onError?: (error: unknown) => void;
    /**
     * 뮤테이션 pending 상태 변화 콜백.
     * delta 1 = 요청 시작, -1 = 요청 종료.
     * 전역 로딩 스피너 등에 활용한다.
     */
    onPendingChange?: (delta: 1 | -1) => void;
    /**
     * axios 응답에서 실제 데이터를 추출하는 함수.
     * 기본값: (res) => res.data.data
     *
     * @example // { code, message, data } 구조일 때
     * extractData: (res) => res.data.data
     *
     * @example // { result } 구조일 때
     * extractData: (res) => res.data.result
     */
    extractData?: <T>(response: AxiosResponse) => T;
}

let _config: ApiHooksConfig | null = null;

/** 전역 설정 등록. 앱 초기화 시 한 번만 호출한다. */
export function configure(config: ApiHooksConfig): void {
    _config = config;
}

/** 내부에서 설정을 가져온다. configure() 미호출 시 오류. */
export function getConfig(): ApiHooksConfig {
    if (!_config) {
        throw new Error(
            "[api-hooks] configure() 를 먼저 호출해야 합니다.\n" +
            "앱 최상단에서 configure({ axios, onError, ... })를 실행하세요."
        );
    }
    return _config;
}

/** 기본 응답 추출 함수 */
export function defaultExtractData<T>(res: AxiosResponse): T {
    return res.data.data as T;
}
